import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Flame, Home } from "lucide-react";
import { type PropertyEnrichment, getScoreColor, getScoreLabel } from "@/hooks/useEnrichment";

interface HotLeadsDrawerProps {
  enrichments: PropertyEnrichment[];
  onSelect: (e: PropertyEnrichment) => void;
}

export function HotLeadsDrawer({ enrichments, onSelect }: HotLeadsDrawerProps) {
  const sorted = [...enrichments].sort((a, b) => b.opportunity_score - a.opportunity_score).slice(0, 25);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border bg-background flex items-center gap-2">
        <Flame className="w-4 h-4 text-orange-500" />
        <h3 className="font-semibold text-sm">Top Houses</h3>
        <Badge variant="secondary" className="ml-auto text-xs">{sorted.length}</Badge>
      </div>
      <ScrollArea className="flex-1">
        {sorted.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            <Home className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Tap any house on the map to enrich it. Top opportunities will rank here.
          </div>
        )}
        <div className="p-2 space-y-1.5">
          {sorted.map((e) => (
            <Card
              key={e.id}
              onClick={() => onSelect(e)}
              className="p-2.5 cursor-pointer hover:bg-accent transition-colors flex items-center gap-2.5"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0"
                style={{ background: getScoreColor(e.opportunity_score) }}
              >
                {e.opportunity_score}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{e.address || `${e.latitude.toFixed(4)}, ${e.longitude.toFixed(4)}`}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {e.est_roof_squares ? `${e.est_roof_squares} sq · ` : ""}
                  {e.est_home_value ? `$${(e.est_home_value / 1000).toFixed(0)}k · ` : ""}
                  {getScoreLabel(e.opportunity_score)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
